<template>
  <div class="container">
    <div class="dropdowns-wrapper">
      <div class="dropdown-container">
        <div class="dropdown-item" style="position: relative;">
          <label for="startDatePicker">Allocation Date <span class="required">*</span></label>
          <div v-if="items4.length > 0 || isSubmitted" class="lock-overlay" @click.stop="showRestrictionError('date')"></div>
          <input
            type="date"
            v-model="currentDate"
            :min="minDate"
            class="date-box"
            :disabled="items4.length > 0 || isSubmitted"
          />
        </div>
      </div>

      <div class="dropdown-container">
        <div class="dropdown-item">
          <label for="fsm-select">Sales Type <span class="required">*</span></label>
          <select id="fsm-select" v-model="selectedSalesType" @change="onSalesTypeChange" :disabled="isSubmitted">
            <option value="" disabled selected>Select Sales Type</option>
            <option v-for="type in salesTypes" :key="type.id" :value="type.id">{{ type.types }}</option>
          </select>
        </div>
      </div>

      <div class="dropdown-container" v-if="selectedSalesType === 2">
        <div class="dropdown-item">
          <label for="dealer-select">Dealer</label>
          <select id="dealer-select" v-model="selectedDealer" :disabled="isSubmitted">
            <option value="" disabled selected>Select Dealer</option>
            <option v-for="dealer in dealers" :key="dealer.profile_id" :value="dealer.profile_id">{{ dealer.name }}</option>
          </select>
        </div>
      </div>

      <div class="dropdown-container" v-if="selectedSalesType === 1">
        <div class="dropdown-item" style="position: relative;">
          <label for="sales-rep-select">Vehicle <span class="required">*</span></label>
          <div v-if="items4.length > 0 || isSubmitted" class="lock-overlay" @click.stop="showRestrictionError('salesRep')"></div>

          <select
              id="sales-rep-select"
              v-model="selectedSalesRep"
              :disabled="items4.length > 0 || isSubmitted"
          >
            <option value="" disabled selected>Select Vehicle</option>
            <option v-for="rep in salesReps" :key="rep.profile_id" :value="rep.profile_id">
              {{ rep.name }}
            </option>
          </select>
        </div>
      </div>
    </div>

    <div class="dropdowns-wrapper">
      <div class="dropdown-container">
        <div class="dropdown-item">
          <label for="item-select">Item Name <span class="required">*</span></label>
          <select id="item-select" v-model="selectedItemName" @change="onItemSelected" :disabled="isSubmitted">
            <option value="" disabled selected>Select Item</option>
            <option v-for="item in availableItems" :key="item.id" :value="item.id">{{ item.item_name }}</option>
          </select>
        </div>
      </div>

      <div class="dropdown-container">
        <div class="dropdown-item">
          <label for="quantity-input">Item Quantity <span class="required">*</span></label>
          <input type="number" id="quantity-input" v-model="itemQuantity" placeholder="Enter quantity" min="0" @keydown.enter="addItem" :disabled="isSubmitted"/>
        </div>
      </div>

      <div class="dropdown-container">
        <div class="dropdown-item">
          <button type="button" class="btn button-default mt-4" @click="addItem" :disabled="isSubmitted">
            {{ isEditing ? 'Update' : 'Add' }}
          </button>
        </div>
      </div>
    </div>

    <div class="col-sm-12 mt-3">
      <div class="card">
        <div class="card-block row">
          <div class="col-sm-12 col-lg-12 col-xl-12">

            <!-- Empty state -->
            <div v-if="items4.length === 0" class="items-empty">
              <i class="fa fa-inbox"></i>
              <p>No items added yet</p>
              <span>Select an item and quantity above, then click Add.</span>
            </div>

            <!-- Items table -->
            <table v-else class="table">
              <thead>
              <tr>
                <th>Item Name</th>
                <th>Quantity</th>
                <th v-if="!isSubmitted">Actions</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(item, index) in items4" :key="index">
                <td>{{ getItemNameById(item.itemName) }}</td>
                <td>{{ item.quantity }}</td>
                <td v-if="!isSubmitted">
                  <i class="fa fa-edit" @click="editItem(index)"></i>
                  <i class="fa fa-trash" @click="confirmDeleteItem(index)"></i>
                </td>
              </tr>
              </tbody>
            </table>

          </div>
        </div>
      </div>
    </div>

    <div class="modal-footer">
      <button
          type="button"
          class="btn btn-outline-secondary"
          :class="isSubmitted ? 'btn-danger' : 'btn-outline-secondary'"
          v-if="showDispatchButton"
          @click="startNewStockAllocation"
      >
        <i class="fa fa-plus mr-1"></i> New Stock Allocation
      </button>

      <div class="modal-footer-right">
        <button type="button" class="btn btn-success" @click="saveStockAllocation" :disabled="saveLoading || isSubmitted">
          <i v-if="saveLoading" class="fa fa-spinner fa-spin mr-2"></i>
          {{ saveLoading ? 'Saving...' : 'Save' }}
        </button>
        <button type="button" class="btn btn-primary" v-if="showDispatchButton" @click="navigateToDispatch">Dispatch</button>
      </div>
    </div>

    <edit-modal
        v-if="showEditModal"
        :show="showEditModal"
        :itemName="editItemName"
        :itemQuantity="editItemQuantity"
        :items="items"
        @close="closeEditModal"
        @update="updateItem"
    ></edit-modal>

    <div v-if="showDeleteModal" class="modal">
      <div class="modal-content-confirmation">
        <span class="close" @click="closeDeleteModal">&times;</span>
        <p>Are you sure you want to delete this item?</p>
        <div class="button-container ">
          <button type="button" class="btn btn-danger" @click="deleteItem">Delete</button>
        </div>
      </div>
    </div>

    <!-- ======================== FLOATING ACTION BUTTON (hidden when panel open) ======================== -->
    <transition name="ds-fab-fade">
      <button
          v-if="!dsPanel.visible"
          class="ds-fab"
          @click="openDeliverySchedule"
          title="View Delivery Schedule"
      >
        <i class="fa fa-calendar-check-o"></i>
        <span class="ds-fab-indicator"></span>
      </button>
    </transition>

    <!-- ======================== DELIVERY SCHEDULE PANEL ======================== -->
    <transition name="ds-slide">
      <div
          v-if="dsPanel.visible"
          class="ds-panel"
          :class="{ 'ds-panel--minimized': dsPanel.minimized }"
          ref="dsPanel"
      >
        <!-- Header -->
        <div
            class="ds-panel__header"
            @click="dsPanel.minimized && toggleMinimize()"
        >
          <!-- Row 1: Title + action buttons -->
          <div class="ds-panel__header-top">
            <div class="ds-panel__title">
              <i class="fa fa-calendar-check-o"></i>
              <span>Delivery Schedule</span>
            </div>
            <div class="ds-panel__actions">
              <button class="ds-icon-btn" @click.stop="toggleMinimize" :title="dsPanel.minimized ? 'Expand' : 'Minimize'">
                <i :class="dsPanel.minimized ? 'fa fa-chevron-up' : 'fa fa-chevron-down'"></i>
              </button>
              <button class="ds-icon-btn ds-icon-btn--close" @click.stop="closeDeliverySchedule" title="Close">
                <i class="fa fa-times"></i>
              </button>
            </div>
          </div>

          <!-- Row 2: Rep name + date (hidden when minimized to save space) -->
          <div class="ds-panel__header-meta" v-show="!dsPanel.minimized">
            <span class="ds-panel__meta-pill" v-if="selectedRepName">
              <i class="fa fa-user"></i> {{ selectedRepName }}
            </span>
            <span class="ds-panel__meta-pill">
              <i class="fa fa-calendar"></i> {{ currentDate }}
            </span>
          </div>
        </div>

        <!-- Body -->
        <div class="ds-panel__body" v-show="!dsPanel.minimized">
          <!-- Loading -->
          <div v-if="dsLoading" class="ds-loading">
            <i class="fa fa-spinner fa-spin"></i>
            <span>Fetching schedules…</span>
          </div>

          <!-- Empty state -->
          <div v-else-if="!dsLoading && deliverySchedules.length === 0" class="ds-empty">
            <i class="fa fa-calendar-times-o"></i>
            <p>No delivery schedules found for this Vehicle &amp; Date.</p>
          </div>

          <!-- Schedule list -->
          <div v-else class="ds-list">
            <div
                v-for="(schedule, idx) in deliverySchedules"
                :key="schedule.scheduleId"
                class="ds-schedule-card"
                :class="{ 'ds-schedule-card--expanded': isScheduleExpanded(schedule.scheduleId) }"
            >
              <!-- Card toggle header (only when multiple schedules) -->
              <div
                  class="ds-schedule-card-header"
                  :class="{ 'ds-schedule-card-header--clickable': deliverySchedules.length > 1 }"
                  @click="deliverySchedules.length > 1 && toggleSchedule(schedule.scheduleId)"
              >
                <div class="ds-schedule-card-header__left">
                  <div class="ds-schedule-index" v-if="deliverySchedules.length > 1">#{{ idx + 1 }}</div>
                  <div class="ds-schedule-header-info">
                    <div class="ds-schedule-name">{{ schedule.scheduleName }}</div>
                    <div class="ds-schedule-meta">
                      <span class="ds-meta-row">
                        <i class="fa fa-calendar"></i>
                        {{ schedule.effectiveFrom }} &rarr; {{ schedule.effectiveTo }}
                      </span>
                      <span class="ds-meta-row ds-meta-row--time">
                        <i class="fa fa-clock-o"></i>
                        {{ schedule.timeWindow }}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                    v-if="deliverySchedules.length > 1"
                    class="ds-toggle-btn"
                    :title="isScheduleExpanded(schedule.scheduleId) ? 'Collapse' : 'Expand'"
                >
                  <i :class="isScheduleExpanded(schedule.scheduleId) ? 'fa fa-chevron-up' : 'fa fa-chevron-down'"></i>
                </button>
              </div>

              <!-- Collapsible body -->
              <div
                  class="ds-schedule-body"
                  v-show="isScheduleExpanded(schedule.scheduleId)"
              >
                <!-- Recurring plan redesign -->
                <div class="ds-recurring-plan" v-if="schedule.recurringPlan">

                  <!-- Type row -->
                  <div class="ds-rp-row">
                    <span class="ds-rp-label">PLAN TYPE</span>
                    <div class="ds-rp-scroll">
                      <span
                          v-for="t in allPlanTypes"
                          :key="t"
                          class="ds-rp-chip ds-rp-chip--type"
                          :class="{ 'ds-rp-chip--inactive': schedule.recurringPlan.type.toLowerCase() !== t.toLowerCase() }"
                      >{{ t.toUpperCase() }}</span>
                    </div>
                  </div>

                  <!-- Months row: show all 12 months, highlight scheduled -->
                  <div class="ds-rp-row" v-if="schedule.recurringPlan.months && schedule.recurringPlan.months.trim()">
                    <span class="ds-rp-label">MONTHS</span>
                    <div class="ds-rp-scroll">
                      <span
                          v-for="m in allMonths"
                          :key="m"
                          class="ds-rp-chip ds-rp-chip--month"
                          :class="{ 'ds-rp-chip--inactive': !isChipActive(schedule.recurringPlan.months, m) }"
                      >{{ m.toUpperCase() }}</span>
                    </div>
                  </div>

                  <!-- Weeks row: show 1-5, highlight scheduled -->
                  <div class="ds-rp-row" v-if="schedule.recurringPlan.weeks && schedule.recurringPlan.weeks.trim()">
                    <span class="ds-rp-label">WEEKS</span>
                    <div class="ds-rp-scroll">
                      <span
                          v-for="w in allWeeks"
                          :key="w"
                          class="ds-rp-chip ds-rp-chip--week"
                          :class="{ 'ds-rp-chip--inactive': !isChipActive(schedule.recurringPlan.weeks, String(w)) }"
                      >{{ w }}</span>
                    </div>
                  </div>

                  <!-- Days row: show all 7 days, highlight scheduled -->
                  <div class="ds-rp-row" v-if="schedule.recurringPlan.days && schedule.recurringPlan.days.trim()">
                    <span class="ds-rp-label">DAYS</span>
                    <div class="ds-rp-scroll">
                      <span
                          v-for="d in allDays"
                          :key="d.full"
                          class="ds-rp-chip ds-rp-chip--day"
                          :class="{ 'ds-rp-chip--inactive': !isDayActive(schedule.recurringPlan.days, d.full) }"
                      >{{ d.short.charAt(0).toUpperCase() }}</span>
                    </div>
                  </div>

                </div>

                <!-- Routes -->
                <div
                    v-for="route in schedule.routes"
                    :key="route.routeId"
                    class="ds-route"
                >
                  <div class="ds-route-name">
                    <span><i class="fa-solid fa-route"></i> {{ route.routeName }}</span>
                    <span class="ds-route-dealer-count">{{ route.dealers ? route.dealers.length : 0 }} Dealers</span>
                  </div>
                  <ul class="ds-dealer-list">
                    <li
                        v-for="dealer in route.dealers"
                        :key="dealer.dealerId"
                        class="ds-dealer-item"
                    >
                      <div class="ds-dealer-name">{{ dealer.dealerName }}</div>
                      <div class="ds-dealer-address">
                        <i class="fa fa-map-marker"></i> {{ dealer.dealerAddress }}
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>

  </div>
</template>

<script>
import { fetchSalesType, fetchDealerTypeList, fetchSalesRepList, fetchItemNameList, saveStockAllocation, fetchOpenStockByItemId, viewDeliverySchedule  } from '@/services/dataService';
import EditModal from '@/pages/Modal/editModal.vue';
import { useToast } from 'vue-toastification';

export default {
  components: {
    EditModal
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    const currentDate = new Date().toISOString().split('T')[0];
    return {
      enableStockValidation: true,
      currentDate,
      minDate: currentDate,
      selectedItemName: "",
      itemQuantity: 0,
      selectedSalesRep: "",
      selectedSalesType: null,
      selectedDealer: "",
      items4: [],
      salesReps: [],
      dealers: [],
      salesTypes: [],
      items: [], // Master API list (Order preserved)
      availableItems: [], // Filtered dropdown list
      localToken: "",
      isEditing: false,
      editIndex: -1,
      loading: false,
      showEditModal: false,
      editItemName: "",
      editItemQuantity: 0,
      showDeleteModal: false,
      deleteIndex: -1,
      showDispatchButton: false,
      openStockData: null,
      saveLoading: false,
      isSubmitted: false,

      // Delivery Schedule Panel
      dsPanel: {
        visible: false,
        minimized: false,
      },
      dsLoading: false,
      deliverySchedules: [],
      expandedSchedules: new Set(),

      // Static chip sets for recurring plan display
      allWeeks: [1, 2, 3, 4, 5],
      allMonths: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
      allDays: [
        { full: 'Monday',    short: 'Mon' },
        { full: 'Tuesday',   short: 'Tue' },
        { full: 'Wednesday', short: 'Wed' },
        { full: 'Thursday',  short: 'Thu' },
        { full: 'Friday',    short: 'Fri' },
        { full: 'Saturday',  short: 'Sat' },
        { full: 'Sunday',    short: 'Sun' },
      ],
      allPlanTypes: ['Day-by-Day', 'Week-by-Week', 'Month-by-Month'],

      // Drag state
      _drag: null,
    };
  },
  methods: {
    async onItemSelected() {
      if (!this.selectedItemName) return;

      this.openStockData = null;

      try {
        const distributorId = localStorage.getItem('profile_id');

        const response = await fetchOpenStockByItemId(this.selectedItemName, distributorId, this.localToken);
        this.openStockData = response.data.data;
      } catch (error) {
        console.error('Error fetching open stock:', error);
        this.openStockData = null;
      }
    },
    showRestrictionError(field) {
      if (field === 'date') {
        this.toast.error("Cannot change the allocation date once items are added.");
      } else if (field === 'salesRep') {
        this.toast.error("Cannot change the sales rep once items are added.");
      }
    },
    async addItem() {
      if (!this.selectedSalesType) {
        this.toast.error("Please select a sales type.");
        return;
      }
      if (this.selectedSalesType === 2 && !this.selectedDealer) {
        this.toast.error("Please select a dealer.");
        return;
      }
      if (this.selectedSalesType === 1 && !this.selectedSalesRep) {
        this.toast.error("Please select a sales rep.");
        return;
      }
      if (!this.selectedItemName) {
        this.toast.error("Please select an item.");
        return;
      }
      if (this.itemQuantity <= 0) {
        this.toast.error("Please enter a valid quantity.");
        return;
      }

      if (this.enableStockValidation) {
        if (!this.openStockData) {
          this.toast.error("Could not retrieve stock information. Please reselect the item.");
          return;
        }
        if (parseInt(this.openStockData.is_active) !== 1) {
          this.toast.error("This item is currently inactive and cannot be allocated.");
          return;
        }
        if (parseFloat(this.itemQuantity) > parseFloat(this.openStockData.open_stock)) {
          this.toast.error(`Quantity exceeds available open stock (${this.openStockData.open_stock}).`);
          return;
        }
      }

      this.loading = true;
      try {
        const existingItem = this.items4.find(item => item.itemName === this.selectedItemName);
        if (existingItem) {
          this.toast.error("Same item already exists in the list.");
        } else {
          // Add to table
          this.items4.push({
            itemName: this.selectedItemName,
            quantity: this.itemQuantity
          });

          // Refresh available items from master list to maintain API order
          this.refreshAvailableItems();

          this.selectedItemName = "";
          this.itemQuantity = 0;
          this.openStockData = null;
        }
      } catch (error) {
        console.error('Error adding item:', error);
      } finally {
        this.loading = false;
      }
    },
    editItem(index) {
      this.editIndex = index;
      this.editItemName = this.items4[index].itemName;
      this.editItemQuantity = this.items4[index].quantity;
      this.showEditModal = true;
    },
    async updateItem(updatedItem) {
      if (updatedItem.itemQuantity > 0) {
        if (this.enableStockValidation) {
          try {
            const distributorId = localStorage.getItem('profile_id');
            const response = await fetchOpenStockByItemId(this.editItemName, distributorId, this.localToken);
            const openStockData = response.data.data;
            
            if (!openStockData) {
              this.toast.error("Could not retrieve stock information for validation.");
              return;
            }
            if (parseInt(openStockData.is_active) !== 1) {
              this.toast.error("This item is currently inactive and cannot be allocated.");
              return;
            }
            if (parseFloat(updatedItem.itemQuantity) > parseFloat(openStockData.open_stock)) {
              this.toast.error(`Quantity exceeds available open stock (${openStockData.open_stock}).`);
              return;
            }
          } catch (error) {
            console.error('Error fetching open stock during update:', error);
            this.toast.error("An error occurred while validating stock.");
            return;
          }
        }
        
        this.items4[this.editIndex].quantity = updatedItem.itemQuantity;
        this.closeEditModal();
      } else {
        this.toast.error("Please enter a valid quantity.");
      }
    },
    closeEditModal() {
      this.showEditModal = false;
      this.editIndex = -1;
      this.editItemName = "";
      this.editItemQuantity = 0;
    },
    confirmDeleteItem(index) {
      this.deleteIndex = index;
      this.showDeleteModal = true;
    },
    deleteItem() {
      if (this.deleteIndex > -1) {
        this.items4.splice(this.deleteIndex, 1);

        // Re-derive the dropdown from the master list to keep original order
        this.refreshAvailableItems();

        this.closeDeleteModal();
      }
    },
    closeDeleteModal() {
      this.showDeleteModal = false;
      this.deleteIndex = -1;
    },
    async fetchAuthToken() {
      const token = localStorage.getItem('token_set');
      if (token) this.localToken = token;
    },
    async loadSalesTypes() {
      try {
        const companyId = localStorage.getItem('company_id');
        const response = await fetchSalesType(companyId, this.localToken);
        if (response.data.status === 'success') {
          this.salesTypes = response.data.data;
          if (this.salesTypes.some(t => t.id === 1)) {
            this.selectedSalesType = 1;
            this.onSalesTypeChange();
          }
        }
      } catch (error) { console.error(error); }
    },
    async loadSalesReps() {
      try {
        const profileId = localStorage.getItem('profile_id');
        const response = await fetchSalesRepList(profileId, this.localToken);
        if (response.data.status === 'success') this.salesReps = response.data.data;
      } catch (error) { console.error(error); }
    },
    async loadDealers() {
      try {
        const profileId = localStorage.getItem('profile_id');
        const response = await fetchDealerTypeList(profileId, this.localToken);
        if (response.data.status === 'success') this.dealers = response.data.data;
      } catch (error) { console.error(error); }
    },
    async loadItems() {
      try {
        const companyId = localStorage.getItem('company_id');
        const response = await fetchItemNameList(companyId, 1, this.localToken);
        if (response.data.status === 'success') {
          this.items = response.data.data; // API Order stored here
          this.refreshAvailableItems();
        }
      } catch (error) { console.error(error); }
    },
    refreshAvailableItems() {
      const selectedIds = this.items4.map(i => i.itemName);
      this.availableItems = this.items.filter(item => !selectedIds.includes(item.id));
    },
    onSalesTypeChange() {
      if (this.selectedSalesType === 2) this.loadDealers();
      else if (this.selectedSalesType === 1) this.loadSalesReps();
    },
    getItemNameById(id) {
      const item = this.items.find(item => item.id === id);
      return item ? item.item_name : '';
    },

    async saveStockAllocation() {
      if (this.items4.length === 0) {
        this.toast.error("Please add at least one item.");
        return;
      }

      this.saveLoading = true;
      try {
        const profileId = localStorage.getItem('profile_id');
        const companyId = localStorage.getItem('company_id');
        const payload = {
          sales_type: this.selectedSalesType,
          profile_id: profileId,
          company_id: companyId,
          item_id: this.items4.map(item => item.itemName),
          qty: this.items4.map(item => item.quantity),
          stock_allocation_date: this.currentDate
        };

        if (this.selectedSalesType === 2) payload.dealer_id = this.selectedDealer;
        else if (this.selectedSalesType === 1) payload.sales_rep_id = this.selectedSalesRep;

        const response = await saveStockAllocation(payload, this.localToken);
        if (response.data.status === 'success') {
          this.isSubmitted = true;
          this.showDispatchButton = true;
          this.toast.success(response.data.message);
          // Form is intentionally NOT reset here — keep submitted data visible
        } else {
          this.toast.error(response.data.message);
        }
      } catch (error) {
        this.toast.error(error.response?.data?.message || error.message);
      } finally {
        this.saveLoading = false;
      }
    },

    navigateToDispatch() {
      this.$router.push({
        path: '/new-dispatch',
        query: {
          vehicle: this.selectedSalesRep
        }
      });
    },

    // Clears everything and starts a fresh allocation
    startNewStockAllocation() {
      this.items4 = [];
      this.selectedItemName = "";
      this.itemQuantity = 0;
      this.selectedSalesRep = "";
      this.selectedDealer = "";
      this.openStockData = null;
      this.currentDate = new Date().toISOString().split('T')[0];
      this.showDispatchButton = false;

      this.refreshAvailableItems();

      // Reset sales type to default (1) and reload its dependent list, same as on mount
      if (this.salesTypes.some(t => t.id === 1)) {
        this.selectedSalesType = 1;
        this.onSalesTypeChange();
      } else {
        this.selectedSalesType = null;
      }
    },

    resetTableData() {
      this.items4 = [];
      this.refreshAvailableItems();
    },

    // ======================== DELIVERY SCHEDULE PANEL ========================

    async openDeliverySchedule() {
      if (!this.selectedSalesRep) {
        this.toast.error("Please select a Vehicle first.");
        return;
      }
      this.dsPanel.visible = true;
      this.dsPanel.minimized = false;
      await this.fetchDeliverySchedules();
    },

    closeDeliverySchedule() {
      this.dsPanel.visible = false;
      this.deliverySchedules = [];
    },

    toggleMinimize() {
      this.dsPanel.minimized = !this.dsPanel.minimized;
    },

    async fetchDeliverySchedules() {
      const repId = this.selectedSalesRep;
      if (!repId) {
        this.deliverySchedules = [];
        this.toast.warning("Please select a Vehicle first to view their delivery schedule.");
        return;
      }

      this.dsLoading = true;
      this.deliverySchedules = [];
      this.expandedSchedules = new Set();
      try {
        const response = await viewDeliverySchedule(repId, this.localToken, this.currentDate);
        if (response.data.status === 'success') {
          this.deliverySchedules = response.data.data || [];
          // Auto-expand: always expand first schedule (or the only one)
          if (this.deliverySchedules.length > 0) {
            this.expandedSchedules = new Set([this.deliverySchedules[0].scheduleId]);
          }
        } else {
          this.deliverySchedules = [];
        }
      } catch (error) {
        console.error('Error fetching delivery schedule:', error);
        this.deliverySchedules = [];
      } finally {
        this.dsLoading = false;
      }
    },

    isScheduleExpanded(scheduleId) {
      if (this.deliverySchedules.length <= 1) return true;
      return this.expandedSchedules.has(scheduleId);
    },

    toggleSchedule(scheduleId) {
      const next = new Set(this.expandedSchedules);
      if (next.has(scheduleId)) {
        next.delete(scheduleId);
      } else {
        next.add(scheduleId);
      }
      this.expandedSchedules = next;
    },

    // Check if a chip value exists in a comma-separated string (e.g. "1, 3, 4")
    isChipActive(csvString, value) {
      if (!csvString) return false;
      const scheduled = csvString.split(',').map(s => s.trim().toLowerCase());
      const valLower = String(value).trim().toLowerCase();
      return scheduled.some(s => s.startsWith(valLower) || valLower.startsWith(s));
    },

    // Check if a day name exists in the scheduled days string (case-insensitive prefix match)
    isDayActive(csvString, fullDayName) {
      if (!csvString) return false;
      const scheduled = csvString.split(',').map(s => s.trim().toLowerCase());
      return scheduled.includes(fullDayName.toLowerCase());
    },
  },
  computed: {
    selectedRepName() {
      if (!this.selectedSalesRep) return '';
      const rep = this.salesReps.find(r => r.profile_id === this.selectedSalesRep);
      return rep ? rep.name : '';
    },
  },
  watch: {
    currentDate(newVal, oldVal) {
      if (newVal !== oldVal && this.dsPanel.visible) {
        this.fetchDeliverySchedules();
      }
    },
    selectedSalesRep(newVal, oldVal) {
      if (newVal !== oldVal && this.dsPanel.visible) {
        this.fetchDeliverySchedules();
      }
    },
  },
  mounted() {
    this.fetchAuthToken().then(() => {
      this.loadSalesTypes();
      this.loadItems();
    });
  },
};
</script>

<style lang="scss" scoped>
/* Styles remain identical to your requirement */
.container {
  margin: 10px;
  background-color: #ffffff;
  border-radius: 8px;
  padding: 32px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);
}

.required { color: #ef4444; margin-left: 2px; font-weight: bold; }

.dropdowns-wrapper {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 24px;
  margin-bottom: 32px;
  align-items: end;
}

.dropdown-container { width: 100%; }

.dropdown-item {
  position: relative;
  display: flex;
  flex-direction: column;

  label {
    font-weight: 700;
    font-size: 0.85rem;
    color: #1f2937;
    margin-bottom: 8px;
  }

  input, select {
    width: 100%;
    padding: 10px 14px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 0.9rem;
    color: #374151;
    background-color: #ffffff;
    box-sizing: border-box;
    transition: all 0.2s ease-in-out;

    &:focus {
      outline: none;
      border-color: #3b82f6;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }

    &:disabled {
      background-color: #f9fafb;
      color: #9ca3af;
      cursor: not-allowed;
    }
  }
}

.lock-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 10;
  cursor: not-allowed;
  background: transparent;
}

.card { background: transparent; border: none; box-shadow: none; margin-top: 10px; }
.card-block { padding: 0; }

/* ======================== ITEMS EMPTY STATE ======================== */
.items-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 48px 16px;
  text-align: center;
  color: #9ca3af;
  background: #f9fafb;
  border-radius: 10px;
  border: 1px dashed #e5e7eb;

  .fa {
    font-size: 2.2rem;
    color: #d1d5db;
    margin-bottom: 4px;
  }

  p {
    margin: 0;
    font-size: 0.95rem;
    font-weight: 600;
    color: #6b7280;
  }

  span {
    font-size: 0.8rem;
    color: #9ca3af;
  }
}

.table {
  width: 100%;
  border-collapse: collapse;
  margin: 0;

  th, td { padding: 16px 8px; text-align: left; }
  th { font-weight: 700; font-size: 0.9rem; color: #1f2937; border-bottom: 2px solid #e5e7eb; }
  td { font-size: 0.9rem; color: #4b5563; border-bottom: 1px solid #f3f4f6; vertical-align: middle; }

  .fa-edit {
    color: #6b7280;
    cursor: pointer;
    margin-right: 16px;
    font-size: 1.1rem;
    transition: color 0.2s;
    &:hover { color: #374151; }
  }

  .fa-trash {
    color: #ef4444;
    cursor: pointer;
    font-size: 1.1rem;
    transition: color 0.2s;
    &:hover { color: #dc2626; }
  }
}

.btn {
  padding: 10px 24px;
  font-size: 0.9rem;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  &:active { transform: scale(0.98); }
}

.button-default {
  background-color: #3b82f6;
  color: white;
  width: 100%;
  &:hover { background-color: #2563eb; }
}

.btn-success { background-color: #10b981; color: white; &:hover { background-color: #059669; } }
.btn-primary { background-color: #3b82f6; color: white; &:hover { background-color: #2563eb; } }
.btn-danger  { background-color: #ef4444; color: white; &:hover { background-color: #dc2626; } }

.loading-spinner { text-align: center; margin: 20px 0; color: #3b82f6; font-size: 1rem; }

.modal-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  margin-top: 32px;
  padding-top: 20px;
}

.modal-footer-right {
  display: flex;
  gap: 12px;
}

.btn-outline-secondary {
  margin-right: auto;
  background-color: #ffffff;
  color: #374151;
  border: 1px solid #d1d5db;
  &:hover { background-color: #f9fafb; border-color: #9ca3af; }
}

.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(17, 24, 39, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.modal-content-confirmation {
  background-color: #ffffff;
  padding: 32px;
  border-radius: 8px;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  text-align: center;
  position: relative;

  p { font-size: 1.05rem; font-weight: 500; margin-bottom: 24px; color: #1f2937; }

  .close {
    position: absolute;
    top: 16px;
    right: 20px;
    font-size: 1.5rem;
    cursor: pointer;
    color: #9ca3af;
    &:hover { color: #1f2937; }
  }

  .button-container { display: flex; justify-content: center; gap: 12px; }
}

/* ======================== FLOATING ACTION BUTTON ======================== */
.ds-fab {
  position: fixed;
  right: 28px;
  bottom: 28px;
  z-index: 1200;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #2563eb;
  color: #fff;
  border: none;
  border-radius: 50%;
  font-size: 1.2rem;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(37, 99, 235, 0.4);
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s;

  &:hover {
    transform: translateY(-3px) scale(1.08);
    box-shadow: 0 8px 24px rgba(37, 99, 235, 0.5);
    background: #1d4ed8;
  }

  &:active {
    transform: scale(0.94);
  }
}

/* FAB fade transition */
.ds-fab-fade-enter-active, .ds-fab-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.ds-fab-fade-enter-from, .ds-fab-fade-leave-to {
  opacity: 0;
  transform: scale(0.7);
}

/* ======================== DELIVERY SCHEDULE PANEL ======================== */
.ds-panel {
  position: fixed;
  right: 24px;
  bottom: 48px; /* sits above the FAB */
  z-index: 1100;
  width: 420px;
  max-width: calc(100vw - 32px);
  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18), 0 2px 8px rgba(0,0,0,0.08);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border: 1px solid #e5e7eb;
  transition: box-shadow 0.2s, bottom 0.25s ease, border-radius 0.25s ease;

  /* LinkedIn-style bottom bar when minimized */
  &--minimized {
    bottom: 0 !important;
    right: 24px !important;
    border-radius: 10px 10px 0 0;
    box-shadow: 0 -2px 12px rgba(0,0,0,0.12);
    width: 320px;

    .ds-panel__header {
      border-radius: 10px 10px 0 0;
      cursor: pointer;
    }
  }
}

.ds-panel__header {
  display: flex;
  flex-direction: column;
  gap: 0;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  color: #fff;
  user-select: none;
  border-radius: 14px 14px 0 0;
  flex-shrink: 0;
  cursor: default;
  overflow: hidden;
}

.ds-panel__header-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px 10px;
}

.ds-panel__header-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 0 14px 10px;
}

.ds-panel__meta-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.72rem;
  font-weight: 500;
  color: rgba(255,255,255,0.75);
  background: rgba(255,255,255,0.1);
  border: 1px solid rgba(255,255,255,0.15);
  border-radius: 20px;
  padding: 3px 10px;

  .fa {
    font-size: 0.65rem;
    opacity: 0.8;
  }
}

.ds-panel__title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  font-size: 0.88rem;
  letter-spacing: 0.01em;
  color: #fff;

  .fa { font-size: 0.95rem; opacity: 0.85; }
}

.ds-panel__actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.ds-icon-btn {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: none;
  background: rgba(255,255,255,0.15);
  color: #fff;
  font-size: 0.85rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;

  &:hover { background: rgba(255,255,255,0.28); }
  &--close:hover { background: rgba(239,68,68,0.7); }
}

.ds-panel__body {
  flex: 1;
  overflow-y: auto;
  max-height: 420px;
  padding: 16px;
  background: #f9fafb;
}

/* Loading */
.ds-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 48px 16px;
  color: #6366f1;
  font-size: 0.9rem;
  font-weight: 500;

  .fa { font-size: 2rem; }
}

/* Empty */
.ds-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 48px 16px;
  color: #9ca3af;
  font-size: 0.9rem;
  text-align: center;

  .fa { font-size: 2rem; color: #d1d5db; }
  p { margin: 0; }
}

/* Schedule card */
.ds-schedule-card {
  background: #fff;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
  margin-bottom: 10px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
  transition: box-shadow 0.2s;

  &:last-child { margin-bottom: 0; }

  &--expanded {
    box-shadow: 0 3px 12px rgba(37,99,235,0.1);
    border-color: #bfdbfe;
  }
}

/* Card toggle header */
.ds-schedule-card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 14px;
  background: #f8faff;
  border-bottom: 1px solid transparent;
  transition: background 0.15s;

  &--clickable {
    cursor: pointer;
    &:hover { background: #eff6ff; }
  }

  .ds-schedule-card--expanded & {
    border-bottom-color: #dbeafe;
    background: #eff6ff;
  }
}

.ds-schedule-card-header__left {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

/* Removed right section — toggle btn now sits directly in header */

.ds-schedule-header-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;
}

.ds-schedule-index {
  font-size: 0.7rem;
  font-weight: 700;
  color: #2563eb;
  background: #dbeafe;
  border-radius: 4px;
  padding: 2px 7px;
  white-space: nowrap;
  flex-shrink: 0;
}

.ds-toggle-btn {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  border: none;
  background: #dbeafe;
  color: #2563eb;
  font-size: 0.72rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, color 0.15s;
  flex-shrink: 0;
  pointer-events: none; /* click handled by parent header */

  .ds-schedule-card--expanded & {
    background: #2563eb;
    color: #fff;
  }
}

.ds-schedule-body {
  padding: 12px 14px 14px;
}

.ds-schedule-header {
  margin-bottom: 8px;
}

.ds-schedule-name {
  font-weight: 700;
  font-size: 0.88rem;
  color: #1f2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Date + time shown below name */
.ds-schedule-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 2px;
}

.ds-meta-row {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.73rem;
  font-weight: 500;
  color: #374151;

  .fa {
    font-size: 0.68rem;
    color: #6b7280;
  }

  &--time {
    color: #7c3aed;
    .fa { color: #7c3aed; }
  }
}

/* Keep .ds-badge for any other uses */
.ds-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;

  .fa { font-size: 0.7rem; }

  &--time {
    background: #ede9fe;
    color: #6d28d9;
  }

  &--date {
    background: #dbeafe;
    color: #1d4ed8;
  }
}

/* ======================== RECURRING PLAN CHIPS ======================== */
.ds-recurring-plan {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
  padding: 10px 12px;
  background: #f8faff;
  border-radius: 8px;
  border: 1px solid #e0e7ff;
}

.ds-rp-row {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  min-width: 0;
}

.ds-rp-label {
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: #9ca3af;
  white-space: nowrap;
}

/* Horizontally scrollable chip strip */
.ds-rp-scroll {
  display: flex;
  align-items: center;
  gap: 6px;
  overflow-x: auto;
  width: 100%;
  padding-bottom: 2px;
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar { display: none; } /* Chrome/Safari */
}

/* Base chip */
.ds-rp-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 0.73rem;
  font-weight: 700;
  border-radius: 20px;
  padding: 3px 12px;
  white-space: nowrap;
  border: 1.5px solid transparent;

  /* TYPE — blue */
  &--type {
    background: #dbeafe;
    color: #1d4ed8;
    border-color: #93c5fd;
  }

  /* MONTH — purple */
  &--month {
    border-radius: 5px;
    background: #f3e8ff;
    color: #7c3aed;
    border-color: #c4b5fd;
  }

  /* WEEK — orange rounded square chips */
  &--week {
    background: #fff7ed;
    color: #c2410c;
    border-color: #fdba74;
    border-radius: 8px;
    width: 30px;
    height: 30px;
    padding: 0;
  }

  /* DAY — green circle chips */
  &--day {
    background: #dcfce7;
    color: #15803d;
    border-color: #86efac;
    border-radius: 50%;
    width: 30px;
    height: 30px;
    padding: 0;
    font-size: 0.68rem;
  }

  /* Inactive / unscheduled chip — dimmed ghost style */
  &--inactive {
    background: #f3f4f6 !important;
    color: #d1d5db !important;
    border-color: #e5e7eb !important;
    font-weight: 500;
  }
}

.ds-route {
  margin-top: 10px;
}

.ds-route-name {
  font-weight: 600;
  font-size: 0.82rem;
  color: #1D2128;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
  padding-bottom: 4px;
  border-bottom: 1px dashed #e0e7ff;
}

.ds-route-dealer-count {
  font-size: 0.7rem;
  color: #6b7280;
  font-weight: 500;
  background: #f3f4f6;
  padding: 1px 6px;
  border-radius: 4px;
}

.ds-dealer-list {
  list-style: none;
  margin: 0;
  padding: 0 0 0 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.ds-dealer-item {
  padding: 6px 10px;
  background: #f9fafb;
  border-radius: 6px;
  border-left: 3px solid #E6E6E6;
}

.ds-dealer-name {
  font-weight: 600;
  font-size: 0.82rem;
  color: #1f2937;
}

.ds-dealer-address {
  font-size: 0.75rem;
  color: #9ca3af;
  margin-top: 2px;
  display: flex;
  align-items: center;
  gap: 4px;

  .fa-map-marker {
    color: #DDDDDD; /* red icon stands out nicely */
    font-size: 0.8rem;
  }
}

/* Panel slide transition */
.ds-slide-enter-active, .ds-slide-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.ds-slide-enter-from, .ds-slide-leave-to {
  opacity: 0;
  transform: translateY(20px) scale(0.97);
}
</style>